/**
 * CrisisWizard — NCTV bommelding-intake, 5 stappen.
 * Ontworpen voor gebruik onder stress: grote knoppen, kalm donker thema.
 * Sla NOOIT op vóór "Verstuur intake" — tussenstand in sessionStorage.
 */

import { useState } from "react";

import { addIncident } from "../lib/firestore-ops";
import {
  CALLER_GENDER_OPTIONS,
  CALLER_VOICE_OPTIONS,
  CALLER_SPEECH_OPTIONS,
  CALLER_ACCENT_OPTIONS,
  BACKGROUND_SOUND_OPTIONS,
  validateStep1,
  validateStep2,
  validateStep3,
  validateStep4,
  validateStep5,
  serializeCrisisReport,
  type BommeldingReport,
  type Step1Data,
  type Step2Data,
  type Step3Data,
  type Step4Data,
  type Step5Data,
} from "../lib/crisis-schema";

// ── SessionStorage ──────────────────────────────────────────────────────────

const STORAGE_KEY = "pm26.crisis.wizard";

interface WizardDraft {
  currentStep: number;
  step1: Step1Data;
  step2: Step2Data;
  step3: Step3Data;
  step4: Step4Data;
  step5: Step5Data;
}

function loadDraft(): WizardDraft | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as WizardDraft) : null;
  } catch {
    return null;
  }
}

function saveDraft(draft: WizardDraft): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* quota overschreden — negeer */
  }
}

function clearDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* negeer */
  }
}

function getInitialDraft(): WizardDraft {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const dateLocal = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return {
    currentStep: 0,
    step1: { threatText: "", receivedAt: dateLocal },
    step2: {
      whenExplode: "",
      whereIs: "",
      howLooksLike: "",
      whyDoingThis: "",
      whoAreYou: "",
      howDoYouKnow: "",
    },
    step3: { gender: "", voice: [], speechStyle: [], accent: "" },
    step4: { sounds: [], notes: "" },
    step5: { operatorName: "", callStart: "", callEnd: "" },
  };
}

// ── Hulpfuncties ────────────────────────────────────────────────────────────

function toggleItem<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
}

const STEP_TITLES = [
  "Bedreigingstekst",
  "Sleutelvragen",
  "Beller-kenmerken",
  "Achtergrondgeluiden",
  "Melder-info",
] as const;

const TOTAL_STEPS = 5;

// ── Wizard ──────────────────────────────────────────────────────────────────

/**
 * Volledige-scherm bommelding-wizard (NCTV-protocol).
 * Props: open / onClose — integreer in de desktop-shell naast de logboek-knop.
 */
export function CrisisWizard({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // ─── State (alle hooks vóór de conditionele return) ───────────────────────
  const [draft, setDraftRaw] = useState<WizardDraft>(
    () => loadDraft() ?? getInitialDraft(),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  function setDraft(updater: (prev: WizardDraft) => WizardDraft): void {
    setDraftRaw((prev) => {
      const next = updater(prev);
      saveDraft(next);
      return next;
    });
  }

  function updateStep1(patch: Partial<Step1Data>): void {
    setDraft((d) => ({ ...d, step1: { ...d.step1, ...patch } }));
    setErrors({});
  }

  function updateStep2(patch: Partial<Step2Data>): void {
    setDraft((d) => ({ ...d, step2: { ...d.step2, ...patch } }));
    setErrors({});
  }

  function updateStep3(patch: Partial<Step3Data>): void {
    setDraft((d) => ({ ...d, step3: { ...d.step3, ...patch } }));
    setErrors({});
  }

  function updateStep4(patch: Partial<Step4Data>): void {
    setDraft((d) => ({ ...d, step4: { ...d.step4, ...patch } }));
  }

  function updateStep5(patch: Partial<Step5Data>): void {
    setDraft((d) => ({ ...d, step5: { ...d.step5, ...patch } }));
    setErrors({});
  }

  function validateCurrentStep(): boolean {
    let result: { valid: boolean; errors: Record<string, string> };
    switch (draft.currentStep) {
      case 0:
        result = validateStep1(draft.step1);
        break;
      case 1:
        result = validateStep2(draft.step2);
        break;
      case 2:
        result = validateStep3(draft.step3);
        break;
      case 3:
        result = validateStep4(draft.step4);
        break;
      case 4:
        result = validateStep5(draft.step5);
        break;
      default:
        return true;
    }
    if (!result.valid) setErrors(result.errors);
    return result.valid;
  }

  function handleNext(): void {
    if (!validateCurrentStep()) return;
    setErrors({});
    setDraft((d) => ({ ...d, currentStep: d.currentStep + 1 }));
  }

  function handleBack(): void {
    setErrors({});
    setDraft((d) => ({ ...d, currentStep: Math.max(0, d.currentStep - 1) }));
  }

  async function handleSubmit(): Promise<void> {
    if (!validateCurrentStep()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const report: BommeldingReport = {
        step1: draft.step1,
        step2: draft.step2,
        step3: draft.step3,
        step4: draft.step4,
        step5: draft.step5,
      };
      const serialized = serializeCrisisReport(report);

      // Basisvelden voor het IncidentEntry-type
      const basePayload = {
        time: Date.now(),
        location: "CP — bommelding",
        severity: "alarm" as const,
        title: "BOMMELDING — NCTV-intake",
        body: serialized,
      };

      // Extra Firestore-velden (buiten IncidentEntry-type, Firestore accepteert ze)
      const extraPayload = {
        kind: "bommelding",
        toelichting: serialized,
        step1: report.step1,
        step2: report.step2,
        step3: report.step3,
        step4: report.step4,
        step5: report.step5,
      };

      await addIncident(
        // De gecombineerde payload bevat meer velden dan IncidentEntry;
        // Firestore slaat ze op — de cast is veilig.
        { ...basePayload, ...extraPayload } as Parameters<
          typeof addIncident
        >[0],
      );

      clearDraft();
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Onbekende fout — probeer opnieuw.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleConfirmedCancel(): void {
    clearDraft();
    setDraftRaw(getInitialDraft());
    setErrors({});
    setSubmitError(null);
    setSubmitted(false);
    setConfirmCancel(false);
    onClose();
  }

  function handleCloseAfterSubmit(): void {
    setDraftRaw(getInitialDraft());
    setSubmitted(false);
    onClose();
  }

  // ─── Conditionele return NA alle hooks ────────────────────────────────────
  if (!open) return null;

  const progressPct = ((draft.currentStep + 1) / TOTAL_STEPS) * 100;

  // ─── Stap-formulieren ─────────────────────────────────────────────────────
  function renderStep() {
    switch (draft.currentStep) {
      // ── Stap 1: Bedreigingstekst ──────────────────────────────────────────
      case 0:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Letterlijke tekst van de beller
              </label>
              <textarea
                className="w-full min-h-[140px] bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-accent/40 resize-none"
                placeholder="Schrijf letterlijk op wat de beller zei..."
                value={draft.step1.threatText}
                onChange={(e) => updateStep1({ threatText: e.target.value })}
                autoFocus
              />
              {errors.threatText && (
                <p className="text-2xs text-risk mt-1">{errors.threatText}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Tijdstip van ontvangst
              </label>
              <input
                type="datetime-local"
                className="w-full bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                value={draft.step1.receivedAt}
                onChange={(e) => updateStep1({ receivedAt: e.target.value })}
              />
              {errors.receivedAt && (
                <p className="text-2xs text-risk mt-1">{errors.receivedAt}</p>
              )}
            </div>
          </div>
        );

      // ── Stap 2: Sleutelvragen ─────────────────────────────────────────────
      case 1: {
        const questions: { key: keyof Step2Data; label: string }[] = [
          { key: "whenExplode", label: "Wanneer ontploft het?" },
          { key: "whereIs", label: "Waar ligt het?" },
          { key: "howLooksLike", label: "Hoe ziet het eruit?" },
          { key: "whyDoingThis", label: "Waarom doet u dit?" },
          { key: "whoAreYou", label: "Wie bent u?" },
          { key: "howDoYouKnow", label: "Hoe weet u dit?" },
        ];
        return (
          <div className="space-y-3">
            {errors._form && (
              <p className="text-2xs text-risk bg-risk/10 rounded-lg px-3 py-2">
                {errors._form}
              </p>
            )}
            {questions.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-zinc-400 mb-1">
                  {label}
                </label>
                <input
                  type="text"
                  className="w-full bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-accent/40"
                  placeholder="Antwoord beller..."
                  value={draft.step2[key]}
                  onChange={(e) =>
                    updateStep2({ [key]: e.target.value } as Partial<Step2Data>)
                  }
                />
              </div>
            ))}
          </div>
        );
      }

      // ── Stap 3: Beller-kenmerken ──────────────────────────────────────────
      case 2:
        return (
          <div className="space-y-5">
            {/* Geslacht */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Geslacht beller
              </label>
              <div className="flex gap-3 flex-wrap">
                {CALLER_GENDER_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm font-medium ${
                      draft.step3.gender === opt
                        ? "border-accent bg-accent/15 text-accent"
                        : "border-white/[0.08] bg-ink-800 text-zinc-400"
                    }`}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      name="gender"
                      value={opt}
                      checked={draft.step3.gender === opt}
                      onChange={() => updateStep3({ gender: opt })}
                    />
                    {opt}
                  </label>
                ))}
              </div>
              {errors.gender && (
                <p className="text-2xs text-risk mt-1">{errors.gender}</p>
              )}
            </div>

            {/* Stem */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Stem
              </label>
              <div className="flex flex-wrap gap-2">
                {CALLER_VOICE_OPTIONS.map((opt) => (
                  <CheckPill
                    key={opt}
                    label={opt}
                    checked={draft.step3.voice.includes(opt)}
                    onChange={() =>
                      updateStep3({
                        voice: toggleItem(draft.step3.voice, opt),
                      })
                    }
                  />
                ))}
              </div>
            </div>

            {/* Spraak */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Spraak
              </label>
              <div className="flex flex-wrap gap-2">
                {CALLER_SPEECH_OPTIONS.map((opt) => (
                  <CheckPill
                    key={opt}
                    label={opt}
                    checked={draft.step3.speechStyle.includes(opt)}
                    onChange={() =>
                      updateStep3({
                        speechStyle: toggleItem(draft.step3.speechStyle, opt),
                      })
                    }
                  />
                ))}
              </div>
            </div>

            {/* Accent */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Accent
              </label>
              <select
                className="w-full bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                value={draft.step3.accent}
                onChange={(e) =>
                  updateStep3({
                    accent: e.target.value as Step3Data["accent"],
                  })
                }
              >
                <option value="">Selecteer accent...</option>
                {CALLER_ACCENT_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      // ── Stap 4: Achtergrondgeluiden ───────────────────────────────────────
      case 3:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-3">
                Achtergrondgeluiden
              </label>
              <div className="grid grid-cols-2 gap-2">
                {BACKGROUND_SOUND_OPTIONS.map((opt) => {
                  const checked = draft.step4.sounds.includes(opt);
                  return (
                    <label
                      key={opt}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${
                        checked
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-white/[0.06] bg-ink-800 text-zinc-400"
                      }`}
                    >
                      {/* Visuele checkbox */}
                      <span
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                          checked ? "border-accent bg-accent" : "border-zinc-600"
                        }`}
                      >
                        {checked && (
                          <svg
                            viewBox="0 0 12 12"
                            className="w-3 h-3 text-ink-950"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="2,6 5,9 10,3" />
                          </svg>
                        )}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() =>
                          updateStep4({
                            sounds: toggleItem(draft.step4.sounds, opt),
                          })
                        }
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Aanvullende notities (optioneel)
              </label>
              <textarea
                className="w-full min-h-[80px] bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-accent/40 resize-none"
                placeholder="Bijv. zwaar autoverkeer, drukke achtergrond..."
                value={draft.step4.notes}
                onChange={(e) => updateStep4({ notes: e.target.value })}
              />
            </div>
          </div>
        );

      // ── Stap 5: Melder-info ───────────────────────────────────────────────
      case 4:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Uw naam (CP-operator)
              </label>
              <input
                type="text"
                className="w-full bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-accent/40"
                placeholder="Voornaam achternaam..."
                value={draft.step5.operatorName}
                onChange={(e) =>
                  updateStep5({ operatorName: e.target.value })
                }
                autoFocus
              />
              {errors.operatorName && (
                <p className="text-2xs text-risk mt-1">{errors.operatorName}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Start gesprek
                </label>
                <input
                  type="time"
                  className="w-full bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                  value={draft.step5.callStart}
                  onChange={(e) =>
                    updateStep5({ callStart: e.target.value })
                  }
                />
                {errors.callStart && (
                  <p className="text-2xs text-risk mt-1">{errors.callStart}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Einde gesprek
                </label>
                <input
                  type="time"
                  className="w-full bg-ink-800 border border-white/[0.08] rounded-xl p-3.5 text-zinc-100 text-base focus:outline-none focus:ring-2 focus:ring-accent/40"
                  value={draft.step5.callEnd}
                  onChange={(e) =>
                    updateStep5({ callEnd: e.target.value })
                  }
                />
                {errors.callEnd && (
                  <p className="text-2xs text-risk mt-1">{errors.callEnd}</p>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Bommelding-intake NCTV-protocol"
    >
      {/* ── Permanent adviesbanner ──────────────────────────────────────── */}
      <div className="bg-risk/10 border-b border-risk/25 px-4 py-3 shrink-0">
        <p className="text-sm text-zinc-200 leading-snug">
          <span className="font-semibold text-risk">Blijf kalm.</span> Houd de
          beller aan de praat. Gebruik het woord{" "}
          <span className="font-semibold">bom</span> niet via de portofoon of
          microfoon.
        </p>
      </div>

      {/* ── Voortgangsbalk ─────────────────────────────────────────────── */}
      {!submitted && (
        <div className="h-1 bg-ink-800 shrink-0">
          <div
            className="h-full bg-accent transition-all duration-300 ease-spring"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      {/* ── Stap-header ────────────────────────────────────────────────── */}
      {!submitted && (
        <div className="px-4 pt-4 pb-2 shrink-0">
          <p className="eyebrow">
            Stap {draft.currentStep + 1} van {TOTAL_STEPS}
          </p>
          <h2 className="text-lg font-semibold text-zinc-100 mt-1">
            {STEP_TITLES[draft.currentStep]}
          </h2>
        </div>
      )}

      {/* ── Formulier-inhoud ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-2">
        {submitted ? (
          <SuccessScreen onClose={handleCloseAfterSubmit} />
        ) : (
          renderStep()
        )}
      </div>

      {/* ── Navigatie ──────────────────────────────────────────────────── */}
      {!submitted && (
        <div className="px-4 pb-6 pt-3 border-t border-white/[0.06] shrink-0 space-y-3">
          {submitError && (
            <p className="text-2xs text-risk bg-risk/10 rounded-lg px-3 py-2">
              {submitError}
            </p>
          )}
          <div className="flex gap-3">
            {draft.currentStep > 0 && (
              <button
                className="flex-1 py-3.5 rounded-xl border border-white/[0.08] text-zinc-300 font-medium text-base disabled:opacity-40 transition-colors"
                onClick={handleBack}
                disabled={submitting}
              >
                Vorige
              </button>
            )}
            {draft.currentStep < TOTAL_STEPS - 1 ? (
              <button
                className="flex-1 py-3.5 rounded-xl bg-accent text-ink-950 font-semibold text-base transition-opacity hover:opacity-90"
                onClick={handleNext}
              >
                Volgende stap
              </button>
            ) : (
              <button
                className="flex-1 py-3.5 rounded-xl bg-risk text-white font-semibold text-base disabled:opacity-50 hover:opacity-90 transition-opacity"
                onClick={() => void handleSubmit()}
                disabled={submitting}
              >
                {submitting ? "Opslaan..." : "Verstuur intake"}
              </button>
            )}
          </div>
          <button
            className="w-full py-2.5 text-zinc-500 text-sm hover:text-zinc-300 transition-colors"
            onClick={() => setConfirmCancel(true)}
            disabled={submitting}
          >
            Annuleren
          </button>
        </div>
      )}

      {/* ── Bevestig-annuleren overlay ──────────────────────────────────── */}
      {confirmCancel && (
        <CancelConfirmOverlay
          onConfirm={handleConfirmedCancel}
          onDismiss={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function CheckPill({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border cursor-pointer transition-colors text-sm ${
        checked
          ? "border-accent bg-accent/15 text-accent"
          : "border-white/[0.06] bg-ink-800 text-zinc-400"
      }`}
    >
      <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}

function SuccessScreen({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-full text-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-ok/15 flex items-center justify-center mb-6">
        <svg
          viewBox="0 0 24 24"
          className="w-8 h-8 text-ok"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-zinc-100 mb-3">
        Intake opgeslagen
      </h2>
      <p className="text-zinc-300 text-base leading-relaxed max-w-xs">
        Alarmeer nu{" "}
        <span className="font-bold text-risk">112</span> en de
        veiligheidsfunctionaris.
      </p>
      <button
        className="mt-8 px-8 py-3.5 rounded-xl bg-ink-800 border border-white/[0.08] text-zinc-200 font-medium text-base hover:bg-ink-750 transition-colors"
        onClick={onClose}
      >
        Sluiten
      </button>
    </div>
  );
}

function CancelConfirmOverlay({
  onConfirm,
  onDismiss,
}: {
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="absolute inset-0 bg-ink-950/90 backdrop-blur-sm flex items-end justify-center z-10 p-4">
      <div className="card w-full max-w-sm p-5">
        <h3 className="text-base font-semibold text-zinc-100 mb-2">
          Intake annuleren?
        </h3>
        <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
          Alle ingevoerde gegevens worden gewist. Dit kan niet ongedaan worden
          gemaakt.
        </p>
        <div className="flex gap-3">
          <button
            className="flex-1 py-3 rounded-xl border border-white/[0.08] text-zinc-300 font-medium text-sm transition-colors"
            onClick={onDismiss}
          >
            Toch niet
          </button>
          <button
            className="flex-1 py-3 rounded-xl bg-risk text-white font-semibold text-sm hover:opacity-90 transition-opacity"
            onClick={onConfirm}
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
}
